'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

interface AuditMetric {
  category: string;
  score: number;
  maxScore: number;
  status: 'excellent' | 'good' | 'satisfactory';
}

const auditData: AuditMetric[] = [
  { category: "Data Encryption", score: 98, maxScore: 100, status: "excellent" },
  { category: "Access Control", score: 96, maxScore: 100, status: "excellent" },
  { category: "Network Security", score: 94, maxScore: 100, status: "excellent" },
  { category: "Incident Response", score: 92, maxScore: 100, status: "excellent" },
  { category: "Compliance", score: 97, maxScore: 100, status: "excellent" },
  { category: "Vulnerability Mgmt", score: 95, maxScore: 100, status: "excellent" }
];

const statusColors = {
  excellent: "#38A169",
  good: "#D69E2E",
  satisfactory: "#4A90E2"
};

interface SecurityAuditResultsProps {
  className?: string;
}

export default function SecurityAuditResults({ className = '' }: SecurityAuditResultsProps) {
  const [selectedMetric, setSelectedMetric] = useState<AuditMetric | null>(null);

  const overallScore = Math.round(
    auditData.reduce((sum, item) => sum + item.score, 0) / auditData.length
  );

  return (
    <section className={`py-16 lg:py-24 bg-background ${className}`}>
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12 lg:mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-xl mb-4">
            <Icon name="DocumentMagnifyingGlassIcon" size={32} className="text-primary" variant="solid" />
          </div>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Independent Security Audit Results
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Third-party security assessment conducted by CyberSec Global in December 2025.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          <div className="lg:col-span-2 bg-card rounded-xl border border-border p-6 lg:p-8">
            <h3 className="font-heading text-xl font-semibold text-foreground mb-6">
              Security Performance Metrics
            </h3>
            
            <div className="w-full h-80" aria-label="Security Audit Scores Bar Chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={auditData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis 
                    dataKey="category" 
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    tick={{ fill: '#4A5568', fontSize: 12 }}
                  />
                  <YAxis 
                    domain={[0, 100]}
                    tick={{ fill: '#4A5568', fontSize: 12 }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#FFFFFF', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px',
                      padding: '12px'
                    }}
                    formatter={(value: number) => [`${value}/100`, 'Score']}
                  />
                  <Bar 
                    dataKey="score" 
                    radius={[8, 8, 0, 0]}
                    onClick={(data) => setSelectedMetric(data)}
                    cursor="pointer"
                  >
                    {auditData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={statusColors[entry.status]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-gradient-to-br from-trust-green to-trust-green/80 rounded-xl p-8 text-white">
              <div className="flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-4 mx-auto">
                <Icon name="CheckBadgeIcon" size={32} className="text-white" variant="solid" />
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold mb-2">{overallScore}%</div>
                <div className="text-lg font-medium opacity-90">Overall Security Score</div>
                <div className="text-sm opacity-75 mt-2">Excellent Rating</div>
              </div>
            </div>

            <div className="bg-card rounded-xl border border-border p-6">
              <h4 className="font-heading text-lg font-semibold text-foreground mb-4">
                Audit Details
              </h4>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <Icon name="CalendarIcon" size={20} className="text-muted-foreground mt-0.5" />
                  <div>
                    <div className="text-sm font-medium text-foreground">Last Audit</div>
                    <div className="text-sm text-muted-foreground">December 15, 2025</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Icon name="BuildingOfficeIcon" size={20} className="text-muted-foreground mt-0.5" />
                  <div>
                    <div className="text-sm font-medium text-foreground">Auditor</div>
                    <div className="text-sm text-muted-foreground">CyberSec Global Ltd.</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Icon name="DocumentTextIcon" size={20} className="text-muted-foreground mt-0.5" />
                  <div>
                    <div className="text-sm font-medium text-foreground">Report ID</div>
                    <div className="text-sm text-muted-foreground">CSG-2025-TG-001</div>
                  </div>
                </div>
              </div>
              <button className="w-full mt-6 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-secondary transition-colors duration-300 font-medium text-sm flex items-center justify-center space-x-2">
                <Icon name="ArrowDownTrayIcon" size={18} />
                <span>Download Full Report</span>
              </button>
            </div>
          </div>
        </div>

        {selectedMetric && (
          <div className="bg-surface rounded-xl border border-border p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h4 className="font-heading text-lg font-semibold text-foreground mb-1">
                  {selectedMetric.category}
                </h4>
                <p className="text-sm text-muted-foreground">
                  Detailed assessment results for this security category
                </p>
              </div>
              <button 
                onClick={() => setSelectedMetric(null)}
                className="p-2 hover:bg-background rounded-lg transition-colors"
              >
                <Icon name="XMarkIcon" size={20} className="text-muted-foreground" />
              </button>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-4xl font-bold text-trust-green">
                {selectedMetric.score}/{selectedMetric.maxScore}
              </div>
              <div className="flex-1">
                <div className="h-3 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-trust-green rounded-full transition-all duration-500"
                    style={{ width: `${(selectedMetric.score / selectedMetric.maxScore) * 100}%` }}
                  />
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  Exceeds industry standards for {selectedMetric.category.toLowerCase()}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}